import React, { useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { Easing, useAnimatedScrollHandler, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import StatusBar from 'design-system/StatusBar';
import { lineHeight } from 'design-system/Text';
import { HEIGHT_SMALL } from 'design-system/Button';
import { PIE_CHART_HEIGHT } from 'design-system/InteractivePieChart';
import { AppView, rem } from 'design-system';
import { useMarginBottom } from 'hooks/useMarginBottom';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useInsightsData from '../hooks/useInsightsData';
import InsightsHeader from '../components/InsightsHeader';
import InsightsRow, { INSIGHTS_ROW_HEIGHT } from '../components/InsightsRow';
import InsightsEmptyComponent from '../components/InsightsEmptyComponent';
import { isWeb } from '../../../constants';
const getItemLayout = (_, index) => ({
    length: INSIGHTS_ROW_HEIGHT,
    offset: INSIGHTS_ROW_HEIGHT * index,
    index,
});
function withAnimation(toValue) {
    'worklet';
    return withTiming(toValue, {
        duration: 300,
        easing: Easing.inOut(Easing.cubic),
    });
}
const BORDER_RADIUS = rem(16);
const TOP_SPACING = PIE_CHART_HEIGHT + rem(24 + 24 + 16 + 8 + 16 + 8 + lineHeight.Text_16 + lineHeight.Text_12) + HEIGHT_SMALL;
const InsightsScreen = withAnimatedNavigationHeader(({ scrollY }) => {
    const [category, setCategory] = useState('SECTOR');
    const [selectedPieSlice, setSelectedPieSlice] = useState(undefined);
    const { data, total, count, sliceIndexMap, defaultSliceKey } = useInsightsData(category);
    const { paddingHorizontalStyle, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    }, []);
    const renderItem = useCallback(({ item, index }) => <InsightsRow index={index} data={item} total={total} count={count}/>, [count, total]);
    const markerAnimatedStyle = useAnimatedStyle(() => {
        const index = selectedPieSlice ? sliceIndexMap[selectedPieSlice] || 0 : 0;
        return {
            top: withAnimation(TOP_SPACING + index * INSIGHTS_ROW_HEIGHT),
            transform: [
                {
                    translateY: -scrollY.value,
                },
            ],
            borderTopLeftRadius: withAnimation(index === 0 ? BORDER_RADIUS : 1),
            borderBottomLeftRadius: withAnimation(index === count - 1 ? BORDER_RADIUS : 1),
        };
    }, [selectedPieSlice]);
    const innerMarkerAnimatedStyle = useAnimatedStyle(() => {
        const index = selectedPieSlice ? sliceIndexMap[selectedPieSlice] : 0;
        const selectedItem = data[index];
        return {
            backgroundColor: selectedItem?.selectedColor || 'transparent',
        };
    }, [selectedPieSlice]);
    return (<AppView type="secondary" withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.FlatList scrollEventThrottle={16} onScroll={scrollHandler} ListHeaderComponent={<InsightsHeader data={data} total={total} count={count} category={category} setCategory={setCategory} sliceIndexMap={sliceIndexMap} defaultSliceKey={defaultSliceKey} selectedPieSlice={selectedPieSlice} setSelectedPieSlice={setSelectedPieSlice}/>} data={data} initialNumToRender={10} renderItem={renderItem} getItemLayout={getItemLayout} contentContainerStyle={paddingBottom} ListEmptyComponent={InsightsEmptyComponent}/>
        {!!data.length && (<Animated.View style={[styles.marker, { marginLeft: rem(16) + (paddingHorizontal || 0) }, markerAnimatedStyle]}>
            <Animated.View style={[styles.innerMarker, innerMarkerAnimatedStyle]}/>
          </Animated.View>)}
      </AppView>);
}, ({ colors }) => ({
    title: 'Insights',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    marker: {
        width: rem(32),
        marginLeft: rem(16),
        overflow: 'hidden',
        position: 'absolute',
        height: INSIGHTS_ROW_HEIGHT,
    },
    innerMarker: {
        flex: 1,
        width: rem(4),
    },
});
export default InsightsScreen;
